/**
 * Firebase Auth bridge — TV-P2-01.
 *
 * Calls the deployed `exchangeKeycloakToken` Cloud Function with the
 * Keycloak access token, gets back a Firebase custom token (with
 * `role` / `orgId` / `orgName` / `tenantId` / `keycloakRoles` claims),
 * and signs into Firebase Auth. After this completes,
 * `firebaseAuth.currentUser` is the same identity as the KC sub and
 * Firestore rules can scope by `request.auth.token.tenantId`.
 *
 * The exchange is single-shot per KC token. Re-call when the KC
 * token refreshes — the custom token itself expires after 1 hour
 * (Firebase default), but the underlying ID token from
 * signInWithCustomToken auto-refreshes via the SDK as long as the
 * session lives.
 */

import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { firebaseAuth, firebaseFunctions } from './config';

let inFlight = null;

/**
 * Sign into Firebase Auth using a Keycloak token. Idempotent + safe
 * to call repeatedly — the second concurrent call piggy-backs on the
 * first in-flight promise so we don't burn callable-fn invocations.
 *
 * @param {string} keycloakToken  KC access token (raw JWT)
 * @param {object} [options]
 * @param {string} [options.launchPartition]
 *   STAB-02 — the FHIR partition this SMART launch targets (from
 *   sessionStore.tenantId, which is populated from the sandman launch
 *   JWT's `tenant` claim). When provided, the Cloud Function writes
 *   `customClaims.tenantId = launchPartition` so Firestore rules
 *   scoped on `request.auth.token.tenantId == {partition}` evaluate
 *   correctly for the launch context — instead of using the static KC
 *   user-attribute claim (billing tenant, e.g. `B2C_DEFAULT`) which
 *   doesn't match the launch partition for B2C patients launching
 *   into a provider's tenant.
 *
 * @returns {Promise<import('firebase/auth').User>}
 */
export async function signInWithKeycloak(keycloakToken, options = {}) {
  if (!keycloakToken) {
    throw new Error('signInWithKeycloak: keycloakToken is required');
  }
  if (!firebaseAuth || !firebaseFunctions) {
    // Firebase init failed at module load (see firebase/config.js).
    // Throw a clear error rather than NPE'ing on .currentUser.
    throw new Error(
      'Firebase is not initialized — VITE_FIREBASE_* env vars missing or invalid. ' +
        'Realtime features are disabled for this build.',
    );
  }

  const { launchPartition } = options;

  // Already signed in? Verify the existing Firebase session actually
  // carries the claims we need. Two failure modes to detect:
  //
  //   1. (TV-P2-01) Pre-tenantId session — Firestore rules will 403
  //      because `request.auth.token.tenantId` is missing.
  //   2. (STAB-02) Existing tenantId doesn't match the launchPartition
  //      we're now trying to scope to. Stale session from a prior
  //      launch into a different partition.
  //
  // Force a re-exchange in both cases.
  if (firebaseAuth.currentUser) {
    try {
      const tokenResult = await firebaseAuth.currentUser.getIdTokenResult();
      const existingTenantId = tokenResult?.claims?.tenantId;
      if (!existingTenantId) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Firebase] existing session is missing the tenantId claim — ' +
            'signing out + re-exchanging the Keycloak token.',
        );
        await signOut(firebaseAuth);
      } else if (launchPartition && existingTenantId !== launchPartition) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Firebase] existing session tenantId="${existingTenantId}" ` +
            `but launch is into "${launchPartition}" — re-exchanging.`,
        );
        await signOut(firebaseAuth);
      } else {
        return firebaseAuth.currentUser;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Firebase] could not inspect existing session, signing out:', err);
      try { await signOut(firebaseAuth); } catch {}
    }
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const exchange = httpsCallable(firebaseFunctions, 'exchangeKeycloakToken');
      // Only include launchPartition in the payload when present so
      // legacy callers (kareManager etc.) keep working unchanged.
      const payload = launchPartition
        ? { keycloakToken, launchPartition }
        : { keycloakToken };
      const res = await exchange(payload);
      const { firebaseToken } = res.data || {};
      if (!firebaseToken) {
        throw new Error('exchangeKeycloakToken returned no firebaseToken');
      }
      const cred = await signInWithCustomToken(firebaseAuth, firebaseToken);
      return cred.user;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Subscribe to Firebase Auth state. Useful for components that need
 * to gate Firestore listeners on the bridge being live.
 *
 * @param {(user: import('firebase/auth').User | null) => void} cb
 * @returns {() => void} unsubscribe
 */
export function onFirebaseAuthChanged(cb) {
  if (!firebaseAuth) {
    // No subscription possible — fire `null` once so callers can
    // settle into the unauthenticated branch.
    queueMicrotask(() => cb(null));
    return () => {};
  }
  return firebaseAuth.onAuthStateChanged(cb);
}
